import { useWindowWidth } from '@acceso/design-system';
import { useEffect, useMemo } from 'react';

import { REVIEW_STATES, TReviewStates } from '@/constants';
import {
  TActiveOptions,
  TActiveOptionsSource,
  TBroadcastActiveOptions,
  TRangeActiveOptions,
} from '@/features/mentions/components/MentionFilters/active-options/active-options';
import { MentionFilterBroadcast } from '@/features/mentions/components/MentionFilters/components/MentionFilterBroadcast';
import { MentionFilterPrograms } from '@/features/mentions/components/MentionFilters/components/MentionFilterPrograms';
import { MentionFilterProjects } from '@/features/mentions/components/MentionFilters/components/MentionFilterProjects';
import { MentionFilterSegmentLogo } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentLogos';
import { MentionFilterSegmentPeople } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentPeople';
import { MentionFilterSegmentReviewState } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentReviewState';
import { MentionFilterSegmentTimeKeepingState } from '@/features/mentions/components/MentionFilters/components/MentionFilterSegmentTimeKeepingState';
import { MentionFilterTags } from '@/features/mentions/components/MentionFilters/components/MentionFilterTags';
import { MentionFilterWorkspaces } from '@/features/mentions/components/MentionFilters/components/MentionFilterWorkspaces';
import { hasAppliedFilters as hasAppliedFilterValues } from '@/features/mentions/components/MentionFilters/components/MentionShowSelectedFilters/hooks/has-applied-filters';
import { MentionShowSelectedFilters } from '@/features/mentions/components/MentionFilters/components/MentionShowSelectedFilters/MentionShowSelectedFilters';
import { useMentionFiltersState } from '@/features/mentions/components/MentionFilters/hooks/useMentionFiltersState';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

type TBroadcastScheduleProgramHeaderFiltersProps = {
  reviewState?: boolean;
  timeKeepingState?: boolean;
  workspaces?: boolean;
  projects?: boolean;
  tags?: boolean;
  logo?: boolean;
  broadcast?: boolean;
  programs?: boolean;
  people?: boolean;
  reviewStateOptions?: TReviewStates[];
} & (
  | {
      /** Narrows the dropdowns to the values present in that list's date range. */
      activeOptionsSource?: TActiveOptionsSource;
      broadcastActiveOptions?: never;
      onActiveOptionsOpenChange?: never;
    }
  | {
      activeOptionsSource?: never;
      /** Narrows the dropdowns that read them to a broadcast's ids, already resolved. */
      broadcastActiveOptions: TBroadcastActiveOptions;
      /** Tells whether one of the dropdowns that read those ids is open. */
      onActiveOptionsOpenChange: (open: boolean) => void;
    }
);

/** The dropdowns narrowed by a broadcast's ids, by their `openFilter` name. */
const BROADCAST_ID_FILTERS = ['EDT', 'Tags', 'SegmentLogo', 'SegmentPeople'];

export function BroadcastScheduleProgramHeaderFilters({
  reviewState = false,
  timeKeepingState = false,
  workspaces = false,
  projects = false,
  tags = false,
  logo = false,
  broadcast = false,
  programs = false,
  people = false,
  reviewStateOptions = REVIEW_STATES,
  activeOptionsSource,
  broadcastActiveOptions,
  onActiveOptionsOpenChange,
}: TBroadcastScheduleProgramHeaderFiltersProps) {
  const { filterRef, openFilter, handleToggle, handleClose } = useMentionFiltersState();
  const { width } = useWindowWidth();
  const hasAppliedFilters = useProgramBroadcastFilters(hasAppliedFilterValues);
  const {
    logos: selectedLogos,
    people: selectedPeople,
    gte,
    lte,
    setFilters: setProgramBroadcastFilters,
  } = useProgramBroadcastFilters();

  const rangeOptions = useMemo<TRangeActiveOptions | undefined>(
    () => (activeOptionsSource ? { source: activeOptionsSource, gte, lte } : undefined),
    [activeOptionsSource, gte, lte]
  );
  const activeOptions: TActiveOptions | undefined = broadcastActiveOptions ?? rangeOptions;

  const broadcastIdsOpen = BROADCAST_ID_FILTERS.includes(openFilter);
  useEffect(() => {
    onActiveOptionsOpenChange?.(broadcastIdsOpen);
  }, [broadcastIdsOpen]);
  useEffect(() => () => onActiveOptionsOpenChange?.(false), []);

  return (
    <>
      <div
        className="flex flex-1 flex-col items-center gap-1 lg:order-1"
        id="parrilla-segments-filter"
      >
        <div
          onClick={e => e.stopPropagation()}
          ref={filterRef}
          className="flex w-full flex-col items-center md:flex-row"
        >
          {reviewState && (
            <MentionFilterSegmentReviewState
              isOpen={openFilter === 'SegmentReview'}
              onToggleFilter={() => handleToggle('SegmentReview')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              reviewStateOptions={reviewStateOptions}
            />
          )}
          {timeKeepingState && (
            <MentionFilterSegmentTimeKeepingState
              isOpen={openFilter === 'SegmentTimeKeeping'}
              onToggleFilter={() => handleToggle('SegmentTimeKeeping')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
            />
          )}
          {broadcast && (
            <MentionFilterBroadcast
              isOpen={openFilter === 'Broadcast'}
              onToggleFilter={() => handleToggle('Broadcast')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={rangeOptions}
            />
          )}
          {programs && (
            <MentionFilterPrograms
              isOpen={openFilter === 'Programs'}
              onToggleFilter={() => handleToggle('Programs')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={rangeOptions}
            />
          )}
          {workspaces && (
            <MentionFilterWorkspaces
              isOpen={openFilter === 'EDT'}
              onToggleFilter={() => handleToggle('EDT')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={activeOptions}
            />
          )}
          {projects && (
            <MentionFilterProjects
              isOpen={openFilter === 'Projects'}
              onToggleFilter={() => handleToggle('Projects')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
            />
          )}
          {tags && (
            <MentionFilterTags
              isOpen={openFilter === 'Tags'}
              onToggleFilter={() => handleToggle('Tags')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={activeOptions}
            />
          )}

          {logo && (
            <MentionFilterSegmentLogo
              isOpen={openFilter === 'SegmentLogo'}
              onToggleFilter={() => handleToggle('SegmentLogo')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={activeOptions}
              value={selectedLogos}
              onChange={next => setProgramBroadcastFilters({ logos: next })}
            />
          )}
          {people && (
            <MentionFilterSegmentPeople
              isOpen={openFilter === 'SegmentPeople'}
              onToggleFilter={() => handleToggle('SegmentPeople')}
              showLabel={width < 768 || width > 1680}
              onClose={handleClose}
              activeOptions={activeOptions}
              value={selectedPeople}
              onChange={next => setProgramBroadcastFilters({ people: next })}
            />
          )}
        </div>
        {hasAppliedFilters && (
          <div className="mb-[-2px] mt-[-2px] flex w-full flex-col md:border-l-4 md:border-accent-active-secondary-10 md:px-size-2">
            <MentionShowSelectedFilters reviewStateOptions={reviewStateOptions} />
          </div>
        )}
      </div>
    </>
  );
}
